/**
 * DepartmentMasterPage.tsx
 * /masters/departments — global EPC functional-department master on the
 * Sovereign-Emerald DataTableShell. List (incl. inactive), add, rename/edit,
 * deactivate/reactivate, and merge.
 */

import { useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Dropdown, Modal, Tag } from 'antd';
import { Building2, GitMerge, MoreHorizontal, Pencil, Plus, Power } from 'lucide-react';
import { toast } from 'react-toastify';

import PageBreadcrumb from '@/shared/PageBreadCrumb';
import { DataTableShell } from '@/components/data-table';
import type { ActiveFilterChip } from '@/components/data-table';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';
import { resolveDeptPresentation } from '@/features/epc/project/team/departmentTemplates';
import { masterId } from '../../../services/epcDepartmentMasterService';

import EditDepartmentModal from './EditDepartmentModal';
import MergeDepartmentModal from './MergeDepartmentModal';
import { useDepartmentMasters, type EpcDepartmentMaster } from './useDepartmentMasters';

type StatusFilter = 'active' | 'inactive' | 'all';

const STATUS_OPTIONS: { label: string; value: StatusFilter }[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'All', value: 'all' },
];

// ─── Row actions (⋯ dropdown; destructive confirm via Modal.confirm) ─────────

interface RowActionsProps {
  dept: EpcDepartmentMaster;
  onEdit: () => void;
  onMerge: () => void;
  onToggle: () => Promise<void>;
}

function RowActions({ dept, onEdit, onMerge, onToggle }: RowActionsProps) {
  const handleDeactivateClick = () => {
    Modal.confirm({
      title: 'Deactivate department?',
      content: `"${dept.name}" will be hidden from new selections. Existing project org units are untouched.`,
      okText: 'Deactivate',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => onToggle(),
    });
  };

  const items = [
    { key: 'edit', label: 'Edit', icon: <Pencil size={14} aria-hidden /> },
    dept.inactive
      ? { key: 'reactivate', label: 'Reactivate', icon: <Power size={14} aria-hidden /> }
      : { key: 'deactivate', label: 'Deactivate', icon: <Power size={14} aria-hidden />, danger: true },
    ...(dept.inactive
      ? []
      : [{ key: 'merge', label: 'Merge into…', icon: <GitMerge size={14} aria-hidden /> }]),
  ];

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items,
        onClick: ({ key }) => {
          if (key === 'edit') onEdit();
          else if (key === 'merge') onMerge();
          else if (key === 'reactivate') void onToggle();
          else if (key === 'deactivate') handleDeactivateClick();
        },
      }}
    >
      <button
        type="button"
        title="Actions"
        aria-label={`Actions for ${dept.name}`}
        className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors duration-150"
        style={{ color: SOVEREIGN.kpiCaption, border: `1px solid ${SOVEREIGN.cardBorder}`, background: SOVEREIGN.inputBg }}
      >
        <MoreHorizontal size={15} aria-hidden />
      </button>
    </Dropdown>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function DepartmentMasterPage() {
  const { query, updateMutation } = useDepartmentMasters();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<EpcDepartmentMaster | 'new' | null>(null);
  const [merging, setMerging] = useState<EpcDepartmentMaster | null>(null);

  const all = useMemo(() => query.data ?? [], [query.data]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((m) => {
        if (status === 'active' && m.inactive) return false;
        if (status === 'inactive' && !m.inactive) return false;
        if (q && !m.name.toLowerCase().includes(q)) return false;
        return true;
      })
      .slice()
      .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999) || a.name.localeCompare(b.name));
  }, [all, search, status]);

  const handleToggle = async (m: EpcDepartmentMaster) => {
    try {
      await updateMutation.mutateAsync({ id: masterId(m), body: { inactive: !m.inactive } });
      toast.success(`Department ${m.inactive ? 'reactivated' : 'deactivated'}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      toast.error(message || 'Failed to update department status');
    }
  };

  const columns = useMemo<ColumnsType<EpcDepartmentMaster>>(
    () => [
      {
        title: 'Department',
        dataIndex: 'name',
        key: 'name',
        render: (_: unknown, m: EpcDepartmentMaster) => {
          const p = resolveDeptPresentation(m.name);
          const Icon = p.icon;
          return (
            <div className="flex items-center gap-2.5 min-w-0">
              <span
                aria-hidden
                className={`flex h-8 w-8 items-center justify-center rounded-lg shrink-0 border bg-gradient-to-br ${p.accent}`}
              >
                <Icon size={15} aria-hidden />
              </span>
              <span
                className={`text-sm font-semibold truncate ${m.inactive ? 'line-through' : ''}`}
                style={{ color: m.inactive ? SOVEREIGN.kpiCaption : SOVEREIGN.ink }}
              >
                {m.name}
              </span>
            </div>
          );
        },
      },
      {
        title: 'Code',
        dataIndex: 'code',
        key: 'code',
        width: 110,
        render: (code: string | undefined) =>
          code ? (
            <span
              className="inline-block text-xs font-mono font-semibold px-2 py-0.5 rounded"
              style={{
                background: SOVEREIGN.inputBg,
                border: `1px solid ${SOVEREIGN.cardBorder}`,
                color: SOVEREIGN.ink,
                letterSpacing: '0.04em',
              }}
            >
              {code}
            </span>
          ) : (
            <span className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
              —
            </span>
          ),
      },
      {
        title: 'Focus areas',
        key: 'focusAreas',
        render: (_: unknown, m: EpcDepartmentMaster) => {
          const areas = m.focusAreas ?? [];
          if (areas.length === 0) {
            return (
              <span className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
                —
              </span>
            );
          }
          const shown = areas.slice(0, 3);
          const extra = areas.length - shown.length;
          return (
            <div className="flex flex-wrap items-center gap-1">
              {shown.map((a) => (
                <Tag key={a} className="!mr-0">
                  {a}
                </Tag>
              ))}
              {extra > 0 && (
                <span className="text-xs font-medium" style={{ color: SOVEREIGN.kpiCaption }}>
                  +{extra}
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: 'Used in',
        key: 'usageCount',
        width: 120,
        render: (_: unknown, m: EpcDepartmentMaster) => {
          const n = m.usageCount ?? 0;
          return (
            <span className="text-sm" style={{ color: n > 0 ? SOVEREIGN.ink : SOVEREIGN.kpiCaption }}>
              {n} project{n === 1 ? '' : 's'}
            </span>
          );
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: 110,
        align: 'center',
        render: (_: unknown, m: EpcDepartmentMaster) =>
          m.inactive ? <Tag>Inactive</Tag> : <Tag color="green">Active</Tag>,
      },
      {
        title: 'Sort',
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        width: 80,
        align: 'center',
        render: (sortOrder: number | undefined) => (
          <span className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
            {sortOrder ?? '—'}
          </span>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 90,
        align: 'center',
        render: (_: unknown, m: EpcDepartmentMaster) => (
          <div className="flex items-center justify-center">
            <RowActions
              dept={m}
              onEdit={() => setEditing(m)}
              onMerge={() => setMerging(m)}
              onToggle={() => handleToggle(m)}
            />
          </div>
        ),
      },
    ],
    // handleToggle is stable per render; updateMutation identity drives it.
    // biome-ignore lint/correctness/useExhaustiveDependencies: handleToggle closes over updateMutation only
    [updateMutation.mutateAsync],
  );

  const activeFilters = useMemo<ActiveFilterChip[]>(() => {
    if (status === 'active') return []; // default view — no chip
    return [{ key: 'status', value: status, display: `Status: ${status === 'all' ? 'All' : 'Inactive'}` }];
  }, [status]);

  return (
    <div className="mx-auto max-w-9xl space-y-6">
      <PageBreadcrumb pageTitle="Departments" />

      <DataTableShell<EpcDepartmentMaster>
        title="Departments"
        subtitle="Global master of EPC functional departments — Team Hierarchy and Schedule reference these names"
        icon={<Building2 size={20} />}
        primaryAction={{
          label: 'Add department',
          icon: <Plus size={16} />,
          onClick: () => setEditing('new'),
        }}
        columns={columns}
        data={rows}
        rowKey={(m) => masterId(m)}
        loading={query.isFetching}
        emptyState={{
          title: 'No departments found',
          description:
            search || status !== 'active'
              ? 'Try adjusting your search or status filter.'
              : 'Get started by adding the first department.',
        }}
        toolbar={{
          search: {
            value: search,
            onChange: setSearch,
            placeholder: 'Search departments by name…',
          },
          filters: [
            {
              key: 'status',
              label: 'Status',
              mode: 'single',
              options: STATUS_OPTIONS,
              value: status,
              onChange: (v) => setStatus((v as StatusFilter) || 'active'),
            },
          ],
          activeFilters,
          onClearFilter: () => setStatus('active'),
          onClearAll: () => setStatus('active'),
        }}
      />

      {editing !== null && (
        <EditDepartmentModal department={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}

      {merging !== null && (
        <MergeDepartmentModal source={merging} candidates={all} onClose={() => setMerging(null)} />
      )}
    </div>
  );
}
